<script lang="ts">
  import { onMount } from 'svelte';
  import { link } from 'svelte-spa-router';
  import { api } from '../../framework/js/api.js';
  import { toast } from '../../framework/js/toast.js';
  import {
    AppPageHeader,
    AppLoadingState,
    StatGrid,
    StatusBadge,
    TabBar,
  } from '../../framework/components/app';
  import type { Stat, Tab } from '../../framework/components/app/appTypes.js';

  interface BillingStats {
    active_subscriptions: number;
    estimated_mrr: number;
    revenue_this_month: number;
    revenue_last_month: number;
    upcoming_renewals_7d: number;
    upcoming_renewals_30d: number;
    trials_ending_7d: number;
    upcoming_amount_7d: number;
    upcoming_amount_30d: number;
    ai_jobs_this_month: number;
    tokens_this_month: number;
    estimated_ai_cost_this_month: number;
    addon_revenue_this_month: number;
  }

  interface PlanBreakdown {
    plan_uuid: string;
    plan_name: string;
    description?: string | null;
    price: number;
    annual_price: number | null;
    included_briefs: number;
    max_team_seats: number | null;
    is_active: boolean;
    is_internal: boolean;
    team_count: number;
    monthly_revenue: number;
  }

  interface UpcomingBilling {
    team_uuid: string;
    team_name: string;
    plan_name: string;
    billing_date: string;
    billing_type: 'renewal' | 'trial_end';
    billing_interval: string;
    subtotal: number;
    tax_amount: number;
    total: number;
    currency: string;
    owner_name?: string | null;
    owner_email?: string | null;
    credits_used?: number;
    credits_included?: number;
  }

  const pageTabs: Tab[] = [
    { value: 'upcoming', label: 'Upcoming billing', icon: 'calendar alternate' },
    { value: 'plans', label: 'Plans', icon: 'credit card' },
    { value: 'usage', label: 'AI usage', icon: 'chart bar' },
  ];

  const upcomingTabs: Tab[] = [
    { value: '7d', label: 'Next 7 days' },
    { value: '30d', label: 'Next 30 days' },
  ];

  let isLoading = true;
  let stats: BillingStats | null = null;
  let plans: PlanBreakdown[] = [];
  let upcoming7d: UpcomingBilling[] = [];
  let upcoming30d: UpcomingBilling[] = [];
  let activeTab: 'upcoming' | 'plans' | 'usage' = 'upcoming';
  let upcomingView: '7d' | '30d' = '7d';

  const moneyFmt = new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  });

  function formatMoney(amount: number, currency?: string): string {
    try {
      return new Intl.NumberFormat(navigator.language, {
        style: 'currency',
        currency: (currency || 'AUD').toUpperCase(),
        minimumFractionDigits: 2,
      }).format(amount);
    } catch {
      return moneyFmt.format(amount);
    }
  }

  function formatDate(value: string): string {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(navigator.language, { dateStyle: 'medium' });
  }

  function formatCredits(included: number): string {
    if (included === -1) return 'Unlimited';
    if (included === 0) return 'Pay as you go';
    return `${included} / period`;
  }

  function formatSeats(max: number | null): string {
    if (max == null) return 'Unlimited';
    return String(max);
  }

  $: totalTeamsOnPlans = plans.reduce((sum, p) => sum + p.team_count, 0);

  $: usageStats = ((): Stat[] => {
    if (!stats) return [];
    return [
      {
        label: 'Jobs this month',
        value: String(stats.ai_jobs_this_month),
        icon: 'cogs',
      },
      {
        label: 'Tokens',
        value: stats.tokens_this_month.toLocaleString(navigator.language),
        icon: 'database',
      },
      {
        label: 'Est. AI cost',
        value: formatMoney(stats.estimated_ai_cost_this_month),
        icon: 'dollar sign',
      },
    ];
  })();

  $: upcomingRows = upcomingView === '7d' ? upcoming7d : upcoming30d;
  $: upcomingCount = upcomingView === '7d' ? stats?.upcoming_renewals_7d : stats?.upcoming_renewals_30d;
  $: upcomingAmount =
    upcomingView === '7d' ? stats?.upcoming_amount_7d : stats?.upcoming_amount_30d;

  onMount(async () => {
    await loadDashboard();
  });

  async function loadDashboard() {
    isLoading = true;
    try {
      const res = (await api.getAdminBillingDashboard()) as {
        success?: boolean;
        message?: string;
        data?: {
          stats?: BillingStats;
          plans?: PlanBreakdown[];
          upcoming_7d?: UpcomingBilling[];
          upcoming_30d?: UpcomingBilling[];
        };
      };
      if (res?.success && res.data) {
        stats = res.data.stats ?? null;
        plans = res.data.plans ?? [];
        upcoming7d = res.data.upcoming_7d ?? [];
        upcoming30d = res.data.upcoming_30d ?? [];
      } else {
        toast.showNotification(false, res?.message || 'Failed to load billing dashboard');
      }
    } catch (err) {
      console.error(err);
      toast.showNotification(false, 'Error loading billing dashboard');
    } finally {
      isLoading = false;
    }
  }
</script>

<div class="admin-billing-page">
  <AppPageHeader
    title="Billing"
    subtitle="Revenue, renewals, and plan breakdown"
  />

  {#if isLoading}
    <AppLoadingState label="Loading billing data…" />
  {:else if stats}
    <div class="summary-grid" aria-label="Billing summary">
      <article class="summary-card">
        <div class="summary-card-head">
          <i class="dollar sign icon" aria-hidden="true"></i>
          <h2 class="summary-card-title">Revenue</h2>
        </div>
        <p class="summary-hero">{formatMoney(stats.estimated_mrr)}</p>
        <p class="summary-hero-label">Estimated MRR</p>
        <dl class="summary-details">
          <div class="summary-detail-row">
            <dt>Collected this month</dt>
            <dd>{formatMoney(stats.revenue_this_month)}</dd>
          </div>
          {#if stats.revenue_last_month > 0}
            <div class="summary-detail-row">
              <dt>Last month</dt>
              <dd>{formatMoney(stats.revenue_last_month)}</dd>
            </div>
          {/if}
          <div class="summary-detail-row">
            <dt>Add-ons this month</dt>
            <dd>{formatMoney(stats.addon_revenue_this_month)}</dd>
          </div>
        </dl>
      </article>

      <article class="summary-card">
        <div class="summary-card-head">
          <i class="users icon" aria-hidden="true"></i>
          <h2 class="summary-card-title">Subscriptions</h2>
        </div>
        <p class="summary-hero">{stats.active_subscriptions}</p>
        <p class="summary-hero-label">Active paid teams</p>
        <dl class="summary-details">
          <div class="summary-detail-row">
            <dt>On catalog plans</dt>
            <dd>{totalTeamsOnPlans}</dd>
          </div>
        </dl>
      </article>

      <article class="summary-card">
        <div class="summary-card-head">
          <i class="calendar alternate icon" aria-hidden="true"></i>
          <h2 class="summary-card-title">Pipeline</h2>
        </div>
        <dl class="summary-details summary-details--primary">
          <div class="summary-detail-row">
            <dt>Due in 7 days</dt>
            <dd>
              <span class="detail-value">{formatMoney(stats.upcoming_amount_7d)}</span>
              <span class="detail-meta">{stats.upcoming_renewals_7d} billings</span>
            </dd>
          </div>
          <div class="summary-detail-row">
            <dt>Due in 30 days</dt>
            <dd>
              <span class="detail-value">{formatMoney(stats.upcoming_amount_30d)}</span>
              <span class="detail-meta">{stats.upcoming_renewals_30d} billings</span>
            </dd>
          </div>
          <div class="summary-detail-row">
            <dt>Trials ending (7d)</dt>
            <dd>
              <span class="detail-value">{stats.trials_ending_7d}</span>
            </dd>
          </div>
        </dl>
      </article>
    </div>

    <section class="main-panel" aria-label="Billing details">
      <TabBar tabs={pageTabs} bind:value={activeTab} />

      {#if activeTab === 'upcoming'}
        <div class="tab-panel admin-list admin-list--nested">
          <div class="tab-panel-head">
            <div>
              <h2 class="tab-panel-title">Scheduled charges</h2>
              {#if upcomingCount != null && upcomingAmount != null}
                <p class="tab-panel-subtitle">
                  {upcomingCount} {upcomingCount === 1 ? 'charge' : 'charges'} · {formatMoney(upcomingAmount)}
                </p>
              {/if}
            </div>
            <TabBar tabs={upcomingTabs} bind:value={upcomingView} />
          </div>

          {#if upcomingRows.length === 0}
            <div class="empty-state" role="status">
              <i class="calendar outline icon empty-icon" aria-hidden="true"></i>
              <p>No billings scheduled in this period.</p>
            </div>
          {:else}
            <div class="table-wrap">
              <table class="data-table">
                <thead>
                  <tr>
                    <th><i class="calendar icon" aria-hidden="true"></i> Date</th>
                    <th><i class="users icon" aria-hidden="true"></i> Team</th>
                    <th><i class="credit card icon" aria-hidden="true"></i> Plan</th>
                    <th><i class="tag icon" aria-hidden="true"></i> Type</th>
                    <th class="num-col"><i class="dollar sign icon" aria-hidden="true"></i> Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {#each upcomingRows as row (row.team_uuid + row.billing_date)}
                    <tr class="data-row">
                      <td class="date-cell">{formatDate(row.billing_date)}</td>
                      <td class="user-cell">
                        <a use:link href="/admin/teams/{row.team_uuid}" class="team-link">{row.team_name}</a>
                        {#if row.owner_name || row.owner_email}
                          <span class="row-meta">{row.owner_name || row.owner_email}</span>
                        {/if}
                      </td>
                      <td>{row.plan_name}</td>
                      <td>
                        <StatusBadge
                          tone={row.billing_type === 'trial_end' ? 'warn' : 'info'}
                          label={row.billing_type === 'trial_end' ? 'Trial end' : 'Renewal'}
                        />
                      </td>
                      <td class="num-col">{formatMoney(row.total, row.currency)}</td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        </div>
      {:else if activeTab === 'plans'}
        <div class="tab-panel admin-list admin-list--nested">
          <div class="tab-panel-head tab-panel-head--single">
            <div>
              <h2 class="tab-panel-title">Plan catalog</h2>
              <p class="tab-panel-subtitle">
                {plans.length} {plans.length === 1 ? 'plan' : 'plans'} · {totalTeamsOnPlans} total teams
              </p>
            </div>
          </div>

          {#if plans.length === 0}
            <div class="empty-state" role="status">
              <i class="credit card icon empty-icon" aria-hidden="true"></i>
              <p>No plans found in the catalog.</p>
            </div>
          {:else}
            <div class="table-wrap plans-table-wrap">
              <table class="data-table plans-table">
                <thead>
                  <tr>
                    <th class="col-plan">Plan</th>
                    <th class="col-money">Monthly</th>
                    <th class="col-money">Annual</th>
                    <th class="col-credits">Credits</th>
                    <th class="col-count">Seats</th>
                    <th class="col-count">Teams</th>
                    <th class="col-money">MRR</th>
                    <th class="col-status">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {#each plans as plan (plan.plan_uuid)}
                    <tr class="data-row">
                      <td class="plan-name-cell">
                        <div class="plan-name-row">
                          <span class="plan-name">{plan.plan_name}</span>
                          {#if plan.is_internal}
                            <StatusBadge tone="neutral" label="Internal" />
                          {/if}
                        </div>
                        {#if plan.description}
                          <span class="plan-desc">{plan.description}</span>
                        {/if}
                      </td>
                      <td class="col-money">{formatMoney(plan.price)}</td>
                      <td class="col-money">
                        {#if plan.annual_price}
                          {formatMoney(plan.annual_price)}
                        {:else}
                          <span class="text-muted">—</span>
                        {/if}
                      </td>
                      <td class="col-credits">{formatCredits(plan.included_briefs)}</td>
                      <td class="col-count">{formatSeats(plan.max_team_seats)}</td>
                      <td class="col-count">{plan.team_count}</td>
                      <td class="col-money">
                        {#if plan.monthly_revenue > 0}
                          {formatMoney(plan.monthly_revenue)}
                        {:else}
                          <span class="text-muted">—</span>
                        {/if}
                      </td>
                      <td class="col-status">
                        <StatusBadge
                          tone={plan.is_active ? 'success' : 'neutral'}
                          label={plan.is_active ? 'Active' : 'Inactive'}
                        />
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        </div>
      {:else}
        <div class="tab-panel usage-panel">
          <div class="tab-panel-head tab-panel-head--single">
            <div>
              <h2 class="tab-panel-title">AI usage this month</h2>
              <p class="tab-panel-subtitle">Background jobs and token spend across all teams</p>
            </div>
            <a use:link href="/admin/credit-usage" class="panel-link">View full credit usage</a>
          </div>

          <StatGrid stats={usageStats} />
        </div>
      {/if}
    </section>
  {/if}
</div>

<style>
  .admin-billing-page {
    padding: var(--space-5) var(--space-6);
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--space-4);
    margin-bottom: var(--space-5);
  }

  .summary-card {
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow-card);
    padding: var(--space-4) var(--space-5);
  }

  .summary-card-head {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-3);
  }

  .summary-card-head :global(i.icon) {
    color: var(--primary);
    font-size: 1.125rem;
    margin: 0;
  }

  .summary-card-title {
    margin: 0;
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--grey-secondary);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .summary-hero {
    margin: 0;
    font-size: var(--text-2xl);
    font-weight: 500;
    color: var(--grey);
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
  }

  .summary-hero-label {
    margin: var(--space-1) 0 var(--space-3);
    font-size: var(--text-xs);
    color: var(--grey-secondary);
  }

  .summary-details {
    margin: 0;
    padding: var(--space-3) 0 0;
    border-top: 1px solid var(--border-color);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .summary-details--primary {
    padding-top: 0;
    border-top: 0;
    gap: var(--space-3);
  }

  .summary-detail-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-3);
  }

  .summary-detail-row dt {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--grey-secondary);
    font-weight: 400;
  }

  .summary-detail-row dd {
    margin: 0;
    text-align: right;
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--grey);
    font-variant-numeric: tabular-nums;
  }

  .detail-value {
    display: block;
  }

  .detail-meta {
    display: block;
    font-size: var(--text-xs);
    font-weight: 400;
    color: var(--grey-secondary);
    margin-top: 2px;
  }

  .main-panel {
    margin-top: var(--space-6);
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow-card);
    padding: var(--space-5) var(--space-6) var(--space-6);
  }

  .main-panel :global(.tab-bar) {
    margin-bottom: var(--space-5);
  }

  .tab-panel-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: var(--space-4);
    flex-wrap: wrap;
    margin-bottom: var(--space-4);
  }

  .tab-panel-head--single {
    align-items: flex-start;
  }

  .tab-panel-head :global(.tab-bar) {
    margin-bottom: 0;
    border-bottom: none;
  }

  .tab-panel-title {
    margin: 0;
    font-size: var(--text-lg);
    font-weight: 500;
    color: var(--grey);
  }

  .tab-panel-subtitle {
    margin: var(--space-1) 0 0;
    font-size: var(--text-sm);
    color: var(--grey-secondary);
    font-variant-numeric: tabular-nums;
  }

  .panel-link {
    font-size: var(--text-sm);
    color: var(--primary);
    text-decoration: none;
    font-weight: 500;
    white-space: nowrap;
    align-self: center;
  }

  .panel-link:hover {
    text-decoration: underline;
  }

  .panel-link:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
    border-radius: 2px;
  }

  .usage-panel :global(.stat-grid) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .plan-name-cell {
    vertical-align: top;
  }

  .plan-name-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .plan-name {
    font-weight: 500;
    color: var(--grey);
  }

  .plan-desc {
    display: block;
    margin-top: var(--space-1);
    font-size: var(--text-xs);
    color: var(--grey-secondary);
    line-height: 1.4;
  }

  /* Plans table — fixed columns, consistent alignment */
  .plans-table-wrap :global(.plans-table) {
    table-layout: fixed;
    width: 100%;
  }

  .plans-table-wrap :global(.plans-table thead th),
  .plans-table-wrap :global(.plans-table tbody td) {
    vertical-align: top;
  }

  .plans-table-wrap :global(.plans-table .col-plan) {
    width: 34%;
    text-align: left;
  }

  .plans-table-wrap :global(.plans-table .col-money) {
    width: 10%;
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .plans-table-wrap :global(.plans-table .col-credits) {
    width: 14%;
    text-align: left;
    white-space: nowrap;
  }

  .plans-table-wrap :global(.plans-table .col-count) {
    width: 7%;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }

  .plans-table-wrap :global(.plans-table .col-status) {
    width: 11%;
    text-align: left;
    white-space: nowrap;
  }

  .plans-table-wrap :global(.plans-table thead th.col-money),
  .plans-table-wrap :global(.plans-table thead th.col-count) {
    text-align: inherit;
  }

  .row-meta {
    display: block;
    font-size: var(--text-xs);
    color: var(--grey-secondary);
    margin-top: 2px;
  }

  @media (max-width: 1100px) {
    .summary-grid {
      grid-template-columns: 1fr;
    }

    .usage-panel :global(.stat-grid) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .plans-table-wrap :global(.plans-table) {
      table-layout: auto;
      min-width: 720px;
    }
  }

  @media (max-width: 768px) {
    .admin-billing-page {
      padding: var(--space-4);
    }

    .main-panel {
      padding: var(--space-4);
    }

    .usage-panel :global(.stat-grid) {
      grid-template-columns: 1fr;
    }

    .tab-panel-head {
      flex-direction: column;
      align-items: stretch;
    }
  }
</style>
